import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Stack,
    Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EventIcon from '@mui/icons-material/Event';
import SchoolIcon from '@mui/icons-material/School';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Fade, Avatar, Grid } from '@mui/material';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Enrollment } from '../types/enrollment';
import EnrollmentModal from '../components/enrollment/EnrollmentModal';
import EnrollmentDetails from '../components/enrollment/EnrollmentDetails';
import EnrollmentEditModal from '../components/enrollment/EnrollmentEditModal';
import EnrollmentPostponeModal from '../components/enrollment/EnrollmentPostponeModal';
import { useToast } from '../context/ToastContext';
import ConfirmDialog from '../components/common/ConfirmDialog';

const EnrollmentManagement = () => {
    const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const { user } = useAuth();
    const [modalOpen, setModalOpen] = useState(false);

    // Action modals state
    const [selectedEnrollment, setSelectedEnrollment] = useState<Enrollment | null>(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [postponeOpen, setPostponeOpen] = useState(false);
    const { showToast } = useToast();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [enrollmentToDelete, setEnrollmentToDelete] = useState<string | null>(null);

    const handleOpenModal = () => setModalOpen(true);
    const handleCloseModal = () => setModalOpen(false);
    const handleCreated = (enrollment: Enrollment) => {
        fetchEnrollments();
        setModalOpen(false);
    };

    const handleView = (enrollment: Enrollment) => {
        setSelectedEnrollment(enrollment);
        setDetailsOpen(true);
    };

    const handleEdit = (enrollment: Enrollment) => {
        setSelectedEnrollment(enrollment);
        setEditOpen(true);
    };

    const handlePostpone = (enrollment: Enrollment) => {
        setSelectedEnrollment(enrollment);
        setPostponeOpen(true);
    };

    const handleUpdated = () => {
        fetchEnrollments();
    };

    useEffect(() => {
        fetchEnrollments();
    }, [statusFilter]);

    const fetchEnrollments = async () => {
        try {
            const params: any = {};
            if (statusFilter) params.status = statusFilter;
            if (search) params.search = search;

            const res = await api.get('/api/enrollment', { params });
            setEnrollments(res.data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching enrollments:', err);
            setLoading(false);
        }
    };

    const handleSearch = () => {
        fetchEnrollments();
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active': return 'success';
            case 'pending': return 'warning';
            case 'completed': return 'info';
            case 'expired': return 'error';
            case 'cancelled': return 'default';
            default: return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'active': return 'Actif';
            case 'pending': return 'En attente';
            case 'completed': return 'Terminé';
            case 'expired': return 'Expiré';
            case 'cancelled': return 'Annulé';
            default: return status;
        }
    };

    const handleDeleteClick = (id: string) => {
        setEnrollmentToDelete(id);
        setConfirmOpen(true);
    };

    const confirmDeleteEnrollment = async () => {
        if (!enrollmentToDelete) return;

        try {
            await api.delete(`/api/enrollment/${enrollmentToDelete}`);
            fetchEnrollments();
            showToast('Inscription supprimée avec succès', 'success');
        } catch (err) {
            console.error('Error deleting enrollment:', err);
            showToast('Erreur lors de la suppression', 'error');
        } finally {
            setConfirmOpen(false);
            setEnrollmentToDelete(null);
        }
    };

    // Calculate generic stats
    const totalEnrollments = enrollments.length;
    const activeEnrollments = enrollments.filter(e => e.status === 'active').length;
    const pendingEnrollments = enrollments.filter(e => e.status === 'pending').length;

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
            p: { xs: 2, md: 4 }
        }}>
            {/* Hero Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 4,
                    p: { xs: 3, md: 5 },
                    mb: 4,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                }} />
                <Box sx={{
                    position: 'absolute',
                    bottom: -30,
                    right: 100,
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.08)',
                }} />

                <Grid container spacing={4} alignItems="center">
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                width: 56,
                                height: 56,
                                backdropFilter: 'blur(10px)'
                            }}>
                                <SchoolIcon sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                                    Gestion des Inscriptions
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                    Suivez et gérez les parcours de formation de vos apprenants
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<AddIcon />}
                            onClick={handleOpenModal}
                            sx={{
                                mt: 2,
                                bgcolor: 'white',
                                color: '#667eea',
                                fontWeight: 700,
                                px: 4,
                                py: 1.5,
                                borderRadius: 3,
                                textTransform: 'none',
                                fontSize: '1rem',
                                boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.95)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Nouvelle Inscription
                        </Button>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 4 }}>
                                <Box sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: 3,
                                    p: 2,
                                    textAlign: 'center',
                                }}>
                                    <Typography variant="h3" sx={{ fontWeight: 700 }}>{totalEnrollments}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Total</Typography>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 4 }}>
                                <Box sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: 3,
                                    p: 2,
                                    textAlign: 'center',
                                }}>
                                    <Typography variant="h3" sx={{ fontWeight: 700 }}>{activeEnrollments}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Actifs</Typography>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 4 }}>
                                <Box sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: 3,
                                    p: 2,
                                    textAlign: 'center',
                                }}>
                                    <Typography variant="h3" sx={{ fontWeight: 700 }}>{pendingEnrollments}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>En attente</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>

            {/* Filters - Clean White Design */}
            <Paper elevation={0} sx={{
                p: 2.5,
                mb: 4,
                bgcolor: 'white', // Darker background for contrast
                borderRadius: '16px',
                border: '1px solid rgba(0, 0, 0, 0.06)',
            }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                        label="Rechercher"
                        variant="outlined"
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        placeholder="Nom de l'apprenant ou parcours..."
                        sx={{
                            flexGrow: 1,
                            fontFamily: 'Quicksand, sans-serif',
                            '& .MuiOutlinedInput-root': {
                                color: 'black',
                                '& fieldset': { borderColor: 'rgba(0,0,0,0.1)' },
                                '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.2)' },
                                '&.Mui-focused fieldset': { borderColor: '#667eea' },
                                borderRadius: '10px'
                            },
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Statut</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Statut"
                            onChange={(e) => setStatusFilter(e.target.value)}
                            sx={{
                                borderRadius: '10px',
                            }}
                        >
                            <MenuItem value="">Tous les statuts</MenuItem>
                            <MenuItem value="pending">En attente</MenuItem>
                            <MenuItem value="active">Actif</MenuItem>
                            <MenuItem value="completed">Terminé</MenuItem>
                            <MenuItem value="expired">Expiré</MenuItem>
                            <MenuItem value="cancelled">Annulé</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        onClick={handleSearch}
                        sx={{
                            background: '#f8f9fa',
                            color: '#666',
                            border: '1px solid #e0e0e0',
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '10px',
                            boxShadow: 'none',
                            '&:hover': {
                                background: '#f0f0f0'
                            }
                        }}
                    >
                        Rechercher
                    </Button>
                </Stack>
            </Paper>

            {/* Table - Clean Design */}
            <Fade in timeout={800}>
                <TableContainer component={Paper} elevation={0} sx={{
                    borderRadius: '16px',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                    overflow: 'hidden',
                }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{
                                bgcolor: '#f8f9fa'
                            }}>
                                <TableCell sx={{ color: '#444', fontWeight: 600, py: 2, fontFamily: 'Quicksand' }}>Apprenant</TableCell>
                                <TableCell sx={{ color: '#444', fontWeight: 600, py: 2, fontFamily: 'Quicksand', display: { xs: 'none', md: 'table-cell' } }}>Parcours</TableCell>
                                <TableCell sx={{ color: '#444', fontWeight: 600, py: 2, fontFamily: 'Quicksand', display: { xs: 'none', sm: 'table-cell' } }}>Début</TableCell>
                                <TableCell sx={{ color: '#444', fontWeight: 600, py: 2, fontFamily: 'Quicksand', display: { xs: 'none', lg: 'table-cell' } }}>Fin</TableCell>
                                <TableCell sx={{ color: '#444', fontWeight: 600, py: 2, fontFamily: 'Quicksand' }}>Statut</TableCell>
                                <TableCell sx={{ color: '#444', fontWeight: 600, py: 2, fontFamily: 'Quicksand' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ color: '#666', py: 6 }}>Chargement...</TableCell>
                                </TableRow>
                            ) : enrollments.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ color: '#666', py: 6 }}>Aucune inscription trouvée</TableCell>
                                </TableRow>
                            ) : (
                                enrollments.map((enrollment) => (
                                    <TableRow
                                        key={enrollment._id}
                                        hover
                                        sx={{
                                            transition: 'background-color 0.2s',
                                            '&:hover': { bgcolor: '#f8f9fa' }
                                        }}
                                    >
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" fontWeight={600} sx={{ color: '#2d3748', fontFamily: 'Quicksand' }}>
                                                    {enrollment.learner ? `${enrollment.learner.firstName} ${enrollment.learner.lastName}` : <span style={{ fontStyle: 'italic', color: '#a0aec0' }}>Utilisateur supprimé</span>}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#718096' }}>
                                                    {enrollment.learner?.email || '-'}
                                                </Typography>
                                                {/* Show parcours title on mobile */}
                                                <Typography variant="caption" sx={{ display: { xs: 'block', md: 'none' }, color: '#4a5568', mt: 0.5 }}>
                                                    {enrollment.parcours?.title}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: '#4a5568', fontFamily: 'Quicksand', display: { xs: 'none', md: 'table-cell' } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar sx={{ width: 24, height: 24, bgcolor: '#ebf4ff', color: '#667eea' }}>
                                                    <SchoolIcon sx={{ fontSize: 14 }} />
                                                </Avatar>
                                                <Typography variant="body2" sx={{ color: '#4a5568' }}>
                                                    {enrollment.parcours?.title || <Typography variant="caption" color="error">Parcours indisponible</Typography>}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ color: '#4a5568', fontFamily: 'Quicksand', display: { xs: 'none', sm: 'table-cell' } }}>
                                            {new Date(enrollment.startDate).toLocaleDateString('fr-FR')}
                                        </TableCell>
                                        <TableCell sx={{ color: '#4a5568', fontFamily: 'Quicksand', display: { xs: 'none', lg: 'table-cell' } }}>
                                            {new Date(enrollment.endDate).toLocaleDateString('fr-FR')}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getStatusLabel(enrollment.status)}
                                                size="small"
                                                icon={enrollment.status === 'active' ? <CheckCircleIcon /> : undefined}
                                                sx={{
                                                    bgcolor: getStatusColor(enrollment.status) === 'success' ? '#def7ec' :
                                                        getStatusColor(enrollment.status) === 'warning' ? '#fefcbf' :
                                                            getStatusColor(enrollment.status) === 'error' ? '#fed7d7' :
                                                                getStatusColor(enrollment.status) === 'info' ? '#bee3f8' :
                                                                    '#edf2f7',
                                                    color: getStatusColor(enrollment.status) === 'success' ? '#03543f' :
                                                        getStatusColor(enrollment.status) === 'warning' ? '#744210' :
                                                            getStatusColor(enrollment.status) === 'error' ? '#742a2a' :
                                                                getStatusColor(enrollment.status) === 'info' ? '#2b6cb0' :
                                                                    '#4a5568',
                                                    fontWeight: 600,
                                                    fontFamily: 'Quicksand',
                                                    border: 'none',
                                                    '& .MuiChip-icon': {
                                                        color: 'inherit',
                                                        fontSize: 16
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1}>
                                                <Tooltip title="Voir détails">
                                                    <IconButton size="small" onClick={() => handleView(enrollment)} sx={{ color: '#718096', '&:hover': { color: '#3182ce', bgcolor: '#ebf8ff' } }}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Modifier">
                                                    <IconButton size="small" onClick={() => handleEdit(enrollment)} sx={{ color: '#718096', '&:hover': { color: '#ecc94b', bgcolor: '#faf5ff' } }}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Reporter">
                                                    <IconButton size="small" onClick={() => handlePostpone(enrollment)} sx={{ color: '#718096', '&:hover': { color: '#dd6b20', bgcolor: '#fffaf0' } }}>
                                                        <EventIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {user?.role === 'super-admin' && (
                                                    <Tooltip title="Supprimer">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteClick(enrollment._id)}
                                                            sx={{ color: '#718096', '&:hover': { color: '#e53e3e', bgcolor: '#fff5f5' } }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Fade>
            <EnrollmentModal
                open={modalOpen}
                onClose={handleCloseModal}
                onCreated={handleCreated}
            />
            <EnrollmentDetails
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                enrollment={selectedEnrollment}
            />
            <EnrollmentEditModal
                open={editOpen}
                onClose={() => setEditOpen(false)}
                enrollment={selectedEnrollment}
                onUpdated={handleUpdated}
            />
            <EnrollmentPostponeModal
                open={postponeOpen}
                onClose={() => setPostponeOpen(false)}
                enrollment={selectedEnrollment}
                onUpdated={handleUpdated}
            />
            <ConfirmDialog
                open={confirmOpen}
                title="Supprimer l'inscription"
                content="Êtes-vous sûr de vouloir supprimer cette inscription ? Cette action est irréversible."
                onConfirm={confirmDeleteEnrollment}
                onCancel={() => setConfirmOpen(false)}
            />
        </Box>
    );
};

export default EnrollmentManagement;
